import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    resource,
    computed,
    signal,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core";
import { Document, DocumentService } from "../../../services/document.service";
import { ReportService } from "../../../services/report.service";
import { S25Util } from "../../../util/s25-util";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { TableBuilder } from "../../bpe/s25.table.builder.component";
import { S25ModalComponent } from "../../s25-modal/s25.modal.component";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { Doc } from "./s25.document.const";
import { Report, Report as Reports } from "../../../pojo/Report";
import { BpeVarsConst } from "../../bpe/s25.bpe.const";
import { TabGroupTab } from "../../../standalone/s25.tab.group.component";
import { VariableI } from "../../../pojo/VariableI";
import { BpeVarsUtil } from "../../bpe/s25.bpe.vars.util";
import { TelemetryService } from "../../../services/telemetry.service";
import { Bind } from "../../../decorators/bind.decorator";
import { DocumentReportBuilderComponent } from "./s25.document.report.builder.component";
import { DocumentUtil } from "./s25.document.util";
import Use = Report.Use;
import ObjectType = Report.ObjectType;
import { ReportBuilderUtil } from "./s25.report.builder.util";

@TypeManagerDecorator("s25-ng-edit-document")
@Component({
    selector: "s25-ng-edit-document",
    template: `
        @if (isInit) {
            <div class="section">
                <label for="document-name" class="ngBold">Document Name</label>
                @if (readOnly) {
                    <span>{{ document.document_name }}</span>
                } @else {
                    <input
                        [(ngModel)]="document.document_name"
                        id="document-name"
                        name="document-name"
                        class="c-input"
                        type="text"
                        maxlength="64"
                    />
                }
                <label for="document-type" class="ngBold">Document Scope </label>
                @if (mode === "edit" || readOnly) {
                    <div>
                        <p class="static-type">{{ type().label }}</p>
                    </div>
                } @else {
                    <s25-generic-dropdown
                        class="type-dropdown"
                        [items]="typeOptions"
                        [placeholder]="'Document Scope'"
                        [chosen]="type()"
                        (chosenChange)="onTypeChange($any($event))"
                    />
                }
                @if (scope !== "subreport") {
                    <label for="output-filename" class="ngBold">Output Filename </label>
                    @if (readOnly) {
                        <span>{{ document.file_name ?? "" }}</span>
                    } @else {
                        <input
                            id="output-filename"
                            name="output-filename"
                            class="c-input"
                            [placeholder]=""
                            [(ngModel)]="document.file_name"
                        />
                    }
                }
                <label for="output-filename" class="ngBold description">Description </label>
                @if (readOnly) {
                    <div [innerHTML]="document.description ?? '' | safeHTML"></div>
                } @else {
                    <s25-ng-rich-text-editor [(modelValue)]="document.description" />
                }
            </div>

            @if (type().type === "standard") {
                @if (readOnly) {
                    <label class="ngBold">Header</label>
                    <div class="rendered" [innerHTML]="document.header_text ?? '' | safeHTML"></div>

                    <label class="ngBold">Body</label>
                    <div class="rendered" [innerHTML]="document.body_text ?? '' | safeHTML"></div>

                    <label class="ngBold">Footer</label>
                    <div class="rendered" [innerHTML]="document.footer_text ?? '' | safeHTML"></div>
                }
                @if (!readOnly) {
                    <label class="ngBold">Header</label>
                    <s25-ng-rich-text-editor
                        [(modelValue)]="document.header_text"
                        [defaultHeight]="300"
                    ></s25-ng-rich-text-editor>

                    <label class="ngBold">Body</label>
                    <s25-ng-rich-text-editor
                        [(modelValue)]="document.body_text"
                        [defaultHeight]="550"
                    ></s25-ng-rich-text-editor>

                    <label class="ngBold">Footer</label>
                    <s25-ng-rich-text-editor
                        [(modelValue)]="document.footer_text"
                        [defaultHeight]="300"
                    ></s25-ng-rich-text-editor>

                    <div
                        class="simple-collapse--wrapper c-margin-top--single templateVarsCollapse c-objectDetails--borderedSection"
                    >
                        <div class="c-sectionHead">
                            <h2>Template Variables</h2>
                        </div>
                        <s25-simple-collapse [defaultCollapsed]="true" [titleText]="'Template Variables'">
                            <div class="c-evddWrapper">
                                <bpe-vars [documentType]="document.document_type" [type]="'document'"></bpe-vars>
                            </div>
                        </s25-simple-collapse>
                    </div>
                }
                <h3 class="ngBold c-margin-top--single">Preview</h3>
                <s25-ng-report-parameters [parameters]="parameters()" (valuesChange)="previewParameters = $event" />
                <s25-ng-button [type]="'outline'" [onClick]="showPreview">Preview</s25-ng-button>
            }
            @if (type().type === "reportBuilder") {
                <div class="section">
                    <label class="ngBold big">Options</label>
                    <div class="frame">
                        <label for="dm-page_orientation">Orientation</label>
                        @if (readOnly) {
                            <div>{{ Doc.pageOrientation[document.page_orientation] ?? "" }}</div>
                        } @else {
                            <select
                                id="dm-page_orientation"
                                class="cn-form__control"
                                [(ngModel)]="document.page_orientation"
                                [disabled]="readOnly"
                            >
                                @for (orientation of Doc.pageOrientationOptions; track $index) {
                                    <option [ngValue]="orientation.val">{{ orientation.txt }}</option>
                                }
                            </select>
                        }

                        <label>Margins</label>
                        <div class="margins">
                            @for (direction of ["Top", "Right", "Bottom", "Left"]; track $index) {
                                <div class="margin">
                                    <label for="dm-margin-{{ direction }}">{{ direction }}:</label>
                                    @if (readOnly) {
                                        <span>{{ margins[$index] ?? 0 }} in</span>
                                    } @else {
                                        <div class="inputWithUnit">
                                            <input
                                                id="dm-margin-{{ direction }}"
                                                type="number"
                                                class="c-input"
                                                [(ngModel)]="margins[$index]"
                                                (ngModelChange)="onMarginsChange()"
                                                placeholder="inches"
                                                step="0.01"
                                                min="0"
                                            />
                                            <span class="unit" title="inches">in</span>
                                        </div>
                                    }
                                </div>
                            }
                        </div>

                        <label for="dm-page_header_height">Header Height</label>
                        @if (readOnly) {
                            <div>{{ document.page_header_height ?? 0 }} em</div>
                        } @else {
                            <div class="inputWithUnit">
                                <input
                                    id="dm-page_header_height"
                                    type="number"
                                    class="c-input"
                                    [(ngModel)]="document.page_header_height"
                                    step="0.1"
                                    min="0"
                                />
                                <span class="unit" title="inches">em</span>
                            </div>
                        }

                        <label for="dm-page_footer_height">Footer Height</label>
                        @if (readOnly) {
                            <div>{{ document.page_footer_height ?? 0 }} em</div>
                        } @else {
                            <div class="inputWithUnit">
                                <input
                                    id="dm-page_footer_height"
                                    type="number"
                                    class="c-input"
                                    [(ngModel)]="document.page_footer_height"
                                    step="0.1"
                                    min="0"
                                />
                                <span class="unit" title="inches">em</span>
                            </div>
                        }

                        <label for="dm-page_footer_height">Apply Date Range Options</label>
                        @if (readOnly) {
                            <ul>
                                @for (item of applyDateRange; track item.itemId) {
                                    <li>{{ item.itemName }}</li>
                                }
                            </ul>
                        } @else {
                            <s25-ng-generic-multiselect-dropdown
                                [(chosen)]="applyDateRange"
                                [items]="applyDateRangeOptions"
                                (done)="onApplyDateRangeChange($event)"
                            />
                        }
                    </div>
                </div>

                <div class="section">
                    <label class="ngBold big">Page Header</label>
                    @if (readOnly) {
                        <div class="rendered" [innerHTML]="document.header_text ?? '' | safeHTML"></div>
                    } @else {
                        <s25-ng-rich-text-editor
                            [(modelValue)]="document.header_text"
                            [defaultHeight]="300"
                        ></s25-ng-rich-text-editor>
                    }
                </div>

                <div class="section">
                    <label class="ngBold big">Page Body</label>
                    <div class="frame">
                        <s25-ng-document-report-builder
                            [document]="document"
                            [type]="type().id"
                            [editable]="!readOnly"
                            (tableStrChange)="document.body_text = $event"
                        />

                        <s25-simple-collapse [headerText]="'Options'" [defaultCollapsed]="true">
                            <div class="options-wrapper">
                                <ng-container [ngTemplateOutlet]="sortOrderTemplate" />

                                <label for="dm-group_by">Group By</label>
                                @if (readOnly) {
                                    <div>{{ Doc.reportBuilderGroupBy[document.group_by] ?? "" }}</div>
                                } @else {
                                    <select id="dm-group_by" class="cn-form__control" [(ngModel)]="document.group_by">
                                        @for (options of Doc.reportBuilderGroupByOptions; track $index) {
                                            <option [ngValue]="options.val">{{ options.txt }}</option>
                                        }
                                    </select>
                                }

                                <label for="dm-page_break">Page Break</label>
                                @if (readOnly) {
                                    <div>{{ Doc.reportBuilderPageBreak[document.page_break] ?? "" }}</div>
                                } @else {
                                    <select
                                        id="dm-page_break"
                                        class="cn-form__control"
                                        [(ngModel)]="document.page_break"
                                    >
                                        @for (options of Doc.reportBuilderPageBreakOptions; track $index) {
                                            <option [ngValue]="options.val">{{ options.txt }}</option>
                                        }
                                    </select>
                                }

                                <label for="dm-font_size">Font Size</label>
                                @if (readOnly) {
                                    <div>{{ document.font_size ?? 0 }} in</div>
                                } @else {
                                    <div class="inputWithUnit">
                                        <input
                                            id="dm-font_size"
                                            type="number"
                                            class="c-input"
                                            [(ngModel)]="document.font_size"
                                            step="0.1"
                                            min="0"
                                        />
                                        <span class="unit" title="em">em</span>
                                    </div>
                                }
                            </div>
                        </s25-simple-collapse>
                    </div>
                </div>

                <div class="section">
                    <label class="ngBold big">Page Footer</label>
                    @if (readOnly) {
                        <div class="rendered" [innerHTML]="document.footer_text ?? '' | safeHTML"></div>
                    } @else {
                        <s25-ng-rich-text-editor
                            [(modelValue)]="document.footer_text"
                            [defaultHeight]="300"
                        ></s25-ng-rich-text-editor>
                    }
                </div>

                @if (!readOnly) {
                    <s25-simple-collapse [defaultCollapsed]="true" [headerText]="'Template Variables'">
                        <div style="padding: 1em;">
                            <p>
                                Select details you would like to include and copy the generated variable values into
                                your <em class="bold">header</em> or <em class="bold">footer</em>. Clicking a variable
                                link will copy that value to your clipboard.
                            </p>

                            <label class="dropdownLabel">Report Variables</label>
                            <s25-ng-bpe-variable
                                [state]="{ options: {} }"
                                [type]="'reservationsHeaderFooter'"
                                [documentType]="document.document_type"
                            />
                        </div>
                    </s25-simple-collapse>
                }

                <h3 class="ngBold c-margin-top--single">Preview</h3>
                <s25-ng-report-parameters [parameters]="parameters()" (valuesChange)="previewParameters = $event" />
                <s25-ng-button [type]="'outline'" [onClick]="showReportBuilderPreview">Preview</s25-ng-button>
            }
            @if (type().type === "subreport") {
                <label class="ngBold">Subreport</label>
                <s25-ng-document-report-builder
                    [document]="document"
                    [type]="type().id"
                    [editable]="!readOnly"
                    (tableStrChange)="document.body_text = $event"
                />

                <ng-container [ngTemplateOutlet]="sortOrderTemplate" />
            }
            @if (type().type === "tableBuilder") {
                @if (view === "form") {
                    @if (type().tableBuilder.length > 1 && view === "form") {
                        <s25-ng-tab-group [tabs]="type().tableBuilder" [(chosen)]="tableTab" />
                    }
                    @for (table of type().tableBuilder; track $index) {
                        @if (table.id === tableTab().id) {
                            @if (type().tableBuilder.length === 1) {
                                <h3 class="ngBold c-margin-top--single">{{ table.label }}</h3>
                            }
                            <s25-ng-table-builder
                                [(columns)]="tables[$index]"
                                [variableType]="table.key"
                                [dateFormatType]="'tableDocumentDateFormats'"
                                [hasDataColumn]="true"
                                [hasWidthColumn]="true"
                                [maxWidth]="10"
                                [editable]="!readOnly"
                                (columnsChange)="onColumnsChange()"
                            ></s25-ng-table-builder>
                        }
                    }
                } @else if (view === "code") {
                    <h3 class="ngBold c-margin-top--single">Data</h3>
                    @if (readOnly) {
                        <div class="rendered">{{ document.body_text ?? "" }}</div>
                    } @else {
                        <textarea [(ngModel)]="document.body_text" class="codeMode"></textarea>
                    }
                }

                @if (!readOnly) {
                    <h3 class="ngBold c-margin-top--single">Advanced</h3>
                    <button class="aw-button aw-button--outline d-block" (click)="toggleView()">
                        {{ view === "form" ? "Code" : "Form" }} View
                    </button>
                }

                <h3 class="ngBold c-margin-top--single">Preview</h3>
                <s25-ng-report-parameters [parameters]="parameters()" (valuesChange)="previewParameters = $event" />
                <s25-ng-button [type]="'outline'" [onClick]="showQueryPreview">Preview</s25-ng-button>
                <s25-ng-modal #confirmExitCodeMode [type]="'confirm'" [title]="'Switch to Form View'">
                    Changes made to code may be lost when switching to Form View. Are you sure that you want to switch?
                </s25-ng-modal>
            }
            @if (isTemplate && mode !== "copy" && !!document.sampleFilename) {
                <label class="ngBold" for="preview-reference">Sample</label>
                <s25-ng-button [type]="'outline'" (click)="onSampleClick()">View Sample</s25-ng-button>
            }

            <div class="buttons c-margin-top--single">
                @if (mode !== "view") {
                    <s25-ng-button [type]="'primary'" [onClick]="onSave">Save</s25-ng-button>
                } @else {
                    <s25-ng-button [type]="'primary'" [onClick]="onCopy">Copy</s25-ng-button>
                }
                <s25-ng-button [type]="'outline'" [onClick]="onCancel">
                    {{ mode !== "view" ? "Cancel" : "Close" }}
                </s25-ng-button>
            </div>
        }

        <ng-template #sortOrderTemplate>
            <label for="dm-sort_order">Sort Order</label>
            <div class="sortOrders">
                @for (sort of sortOrders; track sort) {
                    <div class="sortBy">
                        @if (readOnly && ($first || sortOrders[$index - 1].val !== "none")) {
                            @if (!$first) {
                                <span> then </span>
                            }
                            <div>{{ sortOrders[$index].txt ?? "" }}</div>
                        } @else if (!readOnly) {
                            @if (!$first) {
                                <span> then </span>
                            }
                            <select
                                class="cn-form__control"
                                [(ngModel)]="sortOrders[$index].val"
                                (ngModelChange)="onSortOrderChange()"
                            >
                                <option [ngValue]="'none'" selected>None</option>
                                @for (option of BpeVarsConst.Items[type().id]; track $index) {
                                    @if (option.val && option.sortable !== false) {
                                        <option [ngValue]="option.val">{{ option.txt }}</option>
                                    }
                                }
                            </select>
                        }
                    </div>
                }
            </div>
        </ng-template>
    `,
    styles: `
        label {
            display: block;
        }

        label:not(:first-child) {
            margin-top: 1em;
        }

        .buttons {
            display: flex;
            gap: 0.5em;
        }

        .codeMode {
            width: 100% !important;
            min-height: 5.5em;
            padding: 0.5em;
        }

        ::ng-deep s25-ng-edit-document s25-ng-rich-text-editor .tox-tinymce {
            width: 100% !important;
        }

        #preview-reference {
            margin-right: 0.5em;
        }

        ::ng-deep .s25-multiselect-popup-container {
            max-width: 50vw;
        }

        ::ng-deep .s25-multiselect-popup .s25-multiselect-columns-container {
            max-height: 35vh;
        }

        #output-filename {
            width: min(100%, 20em);
        }

        .static-type {
            padding-top: 0.25rem;
        }

        .rendered {
            background-color: #f6f6f6;
            padding: 0.5rem;
            border: 1px dashed;
            margin-top: 0.25rem;
        }

        s25-ng-tab-group {
            padding-top: 1rem;
        }

        ::ng-deep .nm-party--on s25-ng-edit-document .rendered {
            background-color: #4c4d55;
        }

        .dropdownLabel {
            display: inline-block !important;
            font-weight: bold;
            margin-top: 1em;
            margin-bottom: 0;
            width: min(100%, 225px);
        }

        ::ng-deep s25-ng-edit-document s25-ng-bpe-variable .ngDropdownPaginated {
            max-width: 250px;
        }

        .margins {
            display: flex;
            gap: 1rem;
        }

        .margin {
            display: flex;
            align-items: center;
            gap: 0.25rem;
        }

        .margin label {
            font-style: italic;
        }

        .margin input {
            max-width: 5em;
        }

        .sortOrders {
            display: flex;
            gap: 0.5rem;
            flex-wrap: wrap;
        }

        .sortBy {
            display: flex;
            gap: 0.5rem;
            align-items: center;
        }

        .inputWithUnit {
            display: flex;
            gap: 0.25rem;
            align-items: center;
        }

        .inputWithUnit > input {
            max-width: 5em;
        }

        .type-dropdown {
            max-width: 20em;
            display: block;
        }

        ::ng-deep s25-ng-edit-document .type-dropdown .ngDropdownScrollTopEl {
            max-height: 50dvb;
        }

        .section {
            padding-bottom: 4rem;
        }

        .big {
            font-size: 1.1em;
        }

        .frame {
            border: 1px solid #ccc;
            padding: 1rem;
        }

        .options-wrapper {
            padding: 1rem;
        }

        ::ng-deep .nm-party--on s25-ng-edit-document .frame {
            border-color: #666;
        }

        s25-ng-document-report-builder {
            padding-bottom: 2rem;
            display: block;
        }
        em.bold {
            font-weight: bold;
            font-style: normal;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25EditDocumentComponent implements OnInit {
    @Input() id: number;
    @Input() mode: Doc.Mode = "edit";
    @Input() isTemplate: boolean = false;
    @Input() scope: Doc.Scope;

    // Emits document ID
    @Output() saved = new EventEmitter<number>();
    @Output() copied = new EventEmitter<void>();
    @Output() cancelled = new EventEmitter<void>();

    @ViewChild("confirmExitCodeMode") confirmExitCodeMode: S25ModalComponent;
    @ViewChild(DocumentReportBuilderComponent) reportBuilder: DocumentReportBuilderComponent;

    isInit = false;
    type = signal<Doc.Type & DropDownItem>(undefined);
    document: Document;
    originalDocument: Document;
    defaultFileNames: Record<Doc.TypeReportId, string>;
    tables: TableBuilder.Column[][] = [];
    tableTab = signal<TabGroupTab>(undefined);
    view: "code" | "form" = "form";
    readOnly: boolean = false;
    typeOptions: DropDownItem[] = DocumentUtil.typesToDropdown(Doc.defaultTypes);
    margins: number[] = [0.25, 0.5, 0.17, 0.5];
    sortOrders: { val: string; txt: string }[] = [
        { val: "none", txt: "None" },
        { val: "none", txt: "None" },
        { val: "none", txt: "None" },
    ];
    applyDateRange: DropDownItem[];
    applyDateRangeOptions = [
        { itemId: "Reservation Start", itemName: "Occurrence Start" },
        { itemId: "Pre Event Start", itemName: "Pre Event Start" },
        { itemId: "Event Start", itemName: "Event Start" },
        { itemId: "Task Start", itemName: "Task Start" },
        { itemId: "Pre-Post Dates", itemName: "Pre-Post Dates" },
        { itemId: "Event Dates", itemName: "Event Dates" },
        { itemId: "Reservation Dates", itemName: "Occurrence Dates" },
        { itemId: "Task Dates", itemName: "Task Dates" },
    ];

    readonly shellReport = resource({
        params: () => ({ documentType: this.type() }),
        loader: async ({ params }) => {
            const reportId = params.documentType.reportId;
            if (!reportId) return null;
            const shell = await ReportService.getReportTemplate(reportId);
            if ("message" in shell) return null;
            return shell;
        },
    });
    readonly parameters = computed(() => this.shellReport.value()?.parameter || []);
    previewParameters: Record<Report.Parameter["parameter_field_mapping"], Report.ParameterValue> = {};

    protected readonly Doc = Doc;
    protected readonly BpeVarsConst = BpeVarsConst;

    constructor(private changeDetector: ChangeDetectorRef) {}

    async ngOnInit() {
        if (this.scope === "subreport") {
            this.typeOptions = DocumentUtil.typesToDropdown(Doc.subreportTypes);
        }

        if (this.mode === "view") {
            this.readOnly = true;
        }
        const defaultFileNamesPromise = this.getDefaultFileNames();
        if (this.mode === "create") {
            this.document = this.newDocument();
            await defaultFileNamesPromise; // Only need to await it if we are creating
            this.document.file_name = this.defaultFileNames[Doc.type[this.document.document_type].reportId];
            if (this.scope !== "subreport") {
                this.typeOptions = this.typeOptions.filter((item) =>
                    this.defaultFileNames.hasOwnProperty(item.reportId),
                );
            }
        } else {
            this.document = await DocumentService.getDocument(this.id);
        }
        if (this.mode === "copy") {
            delete this.document.document_id; // No id => new document will be created
            this.document.document_name += " copy";
        }
        const type = Doc.type[this.document.document_type];
        this.type.set({ ...type, itemId: type.id });
        this.originalDocument = S25Util.deepCopy(this.document);
        this.parseDocument();
        this.isInit = true;
        this.changeDetector.detectChanges();
    }

    async getDefaultFileNames() {
        this.defaultFileNames = await ReportService.getDefaultDocumentFileNames();
    }

    newDocument(): Document {
        const document: Document = {
            document_name: "New Document",
            document_type: "event",
        } as Document;
        if (this.scope === "subreport") {
            document.document_type = "rsrvReq";
            document.document_scope = "subreport";
            document.sort_order = this.getDefaultSortOrder(document.document_type);
        }
        return document;
    }

    @Bind
    async showPreview() {
        if (!this.validatePreviewParameters()) return;

        //todo: related events
        //todo: specific organization (billing)
        //todo: calculate total price from eventData (potentially with related events and potentially only for specific org id)
        //todo: override primary org with org id sent it (if any)
        const { header_text, body_text, footer_text } = this.document;
        const runId = await DocumentService.getEventDocumentRunIdFromText(
            header_text,
            body_text,
            footer_text,
            null,
            <number>this.previewParameters.num_parm1,
        );

        const context = { ...this.previewParameters, documentRunId: String(runId) };
        if (this.document.document_type === "event") {
            const id = this.document.report_id || Reports.Reports.EventShell.id;
            return this.putReportRequest(id, "Event Contract", context);
        }
        if (this.document.document_type === "reservation") {
            const id = this.document.report_id || Reports.Reports.ReservationShell.id;
            return this.putReportRequest(id, "Reservation", { ...context });
        }
        if (this.document.document_type === "organization") {
            const id = this.document.report_id || Reports.Reports.OrganizationShell.id;
            return this.putReportRequest(id, "Event" + " Organization", context);
        }
        if (this.document.document_type === "invoice") {
            const id = this.document.report_id || Reports.Reports.PaymentShell.id;
            return this.putReportRequest(id, "Invoice", { ...context });
        }
    }

    async putReportRequest(id: number, name: string, context?: { [key: string]: any }, devMode: boolean = false) {
        const [response, error] = await S25Util.Maybe(
            ReportService.putReportRequest(
                {
                    rpt_id: id,
                    rpt_name: name,
                    rpt_engine: "WS",
                    rpt_use: Use.None,
                    object_type: ObjectType.NonSpecific,
                },
                context,
                null,
                null,
                null,
                null,
                this.document.file_name,
                devMode ? "&dev_mode=T" : "",
            ),
        );
        if (error) return this.onError(error);

        if (typeof response === "string") alert(response);
    }

    @Bind
    async onSave() {
        if (!this.validate()) return;

        if (DocumentUtil.canReferenceDocuments(this.document)) {
            this.document.subreports = ReportBuilderUtil.getSubreportIds(
                this.document.body_text,
                this.type().id as Doc.ReportBuilderTypeId,
            );
        }

        const documentId = await DocumentService.saveDocument(this.document, this.originalDocument);
        if (!documentId) return;

        const telemetrySub = this.isTemplate ? "DM_Documents" : "DM_Templates";
        const telemetryType = `DM_${S25Util.firstCharToUpper(this.mode)}`;
        TelemetryService.sendWithSub("DocMgmt", telemetrySub, telemetryType);

        this.saved.emit(documentId);
    }

    validate() {
        const doc = this.document;
        let message: string;
        if (!doc.document_name) message = "Please enter a name";
        else if (!doc.body_text) message = "Please enter a body";
        else if (this.type().type === "tableBuilder" && this.view === "form") {
            // Check that columns requiring an ID have one
            outer: for (const table of this.tables) {
                for (const column of table) {
                    const requireData = column.variable?.dataType && column.variable.dataType !== "format";
                    if (!column.variable) message = `Please select a variable for column ${column.header}`;
                    if (requireData && !column.data) message = `Please select an item for column ${column.header}`;
                    if (message) break outer;
                }
            }
        } else if (
            (this.type().type === "reportBuilder" || this.type().type === "subreport") &&
            !this.reportBuilder.validate()
        ) {
            message = "Please fix the table errors before saving";
        } else if (this.type().type === "reportBuilder" && !this.applyDateRange?.length) {
            message = "Please select at least one option for Apply Date Range";
        }

        if (message) {
            alert(message);
            return false;
        }
        return true;
    }

    @Bind
    onCopy() {
        this.copied.emit();
    }

    @Bind
    onCancel() {
        this.cancelled.emit();
    }

    async onError(error: any) {
        const text = (await error?.error?.text?.()) as string;
        if (text?.includes("An error occurred during transform processing")) {
            const msg = `Something went wrong while processing the document.<br>Please make sure that the document is valid and try again.`;
            alert(msg);
            return;
        }
    }

    onTypeChange(type: Doc.Type) {
        // reset header, text and footer when type switch from an excel type
        if (this.type().type === "tableBuilder") {
            this.document.header_text = "";
            this.document.body_text = "";
            this.document.footer_text = "";
        }

        // If file name is still default, update it to the new default file name
        if (this.document.file_name === this.defaultFileNames[this.type().reportId]) {
            this.document.file_name = this.defaultFileNames[type.reportId];
        }
        // Update variables
        this.type.set(type);
        this.document.document_type = type.id;

        // If new type is table builder, set default columns
        if (type.type === "tableBuilder") {
            this.view = "form";
            this.tables = type.tableBuilder.map((table) => {
                const variables = BpeVarsConst.Items[table.key].filter((item) => !item.isGroup);
                return variables.map((item) => ({
                    header: item.txt,
                    variable: item,
                    width: item.width,
                }));
            });
            this.tableTab.set(type.tableBuilder[0]);
            this.updateQueryVariable();
            this.document.file_name = this.document.file_name.replace(/.pdf$/, ".xml"); // Switch extension from pdf to xml
        }

        // If new type is report builder, set default layout properties
        if (type.type === "reportBuilder") {
            this.document.group_by = "none";
            this.document.page_break = "none";
            this.document.margins = "0.25in 0.5in 0.17in 0.5in";
            this.document.font_size = 0.9;
            this.document.page_orientation = "portrait";
            this.document.page_header_height = 3;
            this.document.page_footer_height = 2.5;
            this.document.sort_order = this.getDefaultSortOrder(this.type().id);
            this.document.apply_date_range = `Reservation Start,Pre Event Start,Event Start,Task Start,Reservation Dates,Pre-Post Dates,Event Dates,Task Dates`;
            this.parseDocument();
        } else if (type.type === "subreport") {
            this.document.sort_order = this.getDefaultSortOrder(this.type().id);
            this.parseDocument();
        }
    }

    async toggleView() {
        if (this.view === "code") {
            const ok = await this.confirmExitCodeMode.open();
            if (!ok) return;
            this.view = "form";
            this.parseDocument();
            this.changeDetector.detectChanges();
        } else {
            this.view = "code";
        }
    }

    onColumnsChange() {
        this.updateQueryVariable();
    }

    updateQueryVariable() {
        const type = this.type();
        if (type.type !== "tableBuilder") return;

        this.document.body_text = this.tables
            .map((table, i) => {
                const columns = BpeVarsUtil.getTableBuilderColumnsStr(table, true);
                if (!columns) return "";
                return `{{for: ${type.tableBuilder[i].variable}: ${columns} :end-for}}`;
            })
            .join("\n\n");
    }

    @Bind
    async showQueryPreview() {
        if (!this.validatePreviewParameters()) return;

        const [documentRun, err] = await S25Util.Maybe(
            DocumentService.setDocumentRun(this.document.document_id, "", this.document.body_text, ""),
        );
        if (err) return this.onError(err);

        const context = { ...this.previewParameters, documentRunId: String(documentRun.root.itemId) };

        return this.putReportRequest(this.document.report_id || this.type().reportId, this.type().label, context, true);
    }

    validatePreviewParameters() {
        for (const param of this.parameters()) {
            if (param.required === "F") continue;
            const value = this.previewParameters[param.parameter_field_mapping];
            if (S25Util.isDefined(value)) continue;
            let paramName = Doc.parameterNameMap[param.parameter_name] ?? param.parameter_name;
            alert(`Please enter a value for parameter ${paramName}`);
            return false;
        }
        return true;
    }

    findVariableAndData(variables: VariableI[], varStr: string) {
        for (const variable of variables) {
            if ("dataType" in variable) {
                // Need to match to variable and data, for example
                // deposit.paymentStatus => $data.paymentStatus, data = deposit
                // totalTaxType.123 => totalTaxType.$data, data = 123
                const reVar = S25Util.escapeRegExp(variable.val).replace(/\\\$data/g, "(.*)");
                const regex = new RegExp(`^${reVar}$`);
                const match = varStr.trim().match(regex);
                if (!match) continue;

                const data = match[1]; // First matched $data
                return { variable, data };
            } else if (varStr.trim() === variable.val) {
                return { variable };
            }
        }
        return {};
    }

    parseDocument() {
        this.parseMargins();
        this.parseSortOrder();
        this.parseApplyDateRange();
        // If the type is table builder then we need to parse the body text to get the columns
        const type = this.type();
        if (type.type === "tableBuilder") return this.parseTableBuilder();
    }

    parseTableBuilder() {
        const type = this.type();
        if (type.type !== "tableBuilder") return;

        this.tableTab.set(type.tableBuilder[0]);
        this.tables = new Array(type.tableBuilder.length).fill(null).map((_) => [] as TableBuilder.Column[]);
        for (const { groups } of Array.from(this.document.body_text.matchAll(Doc.tableBuilderRegex))) {
            const i = type.tableBuilder.findIndex((table) => table.variable === groups.variable);
            const table = type.tableBuilder[i];
            if (!table) continue;

            const variables = BpeVarsConst.Items[table.key];
            const columns: TableBuilder.Column[] = [];
            const tableMatches = Array.from(groups.table.matchAll(Doc.tableBuilderColumnRegex));
            for (const { groups } of tableMatches) {
                const { variable, data } = this.findVariableAndData(variables, groups.variable);
                if (!variable) continue;

                const formatName = groups.format?.trim() || "";
                const format = formatName && BpeVarsConst.Items.dateFormats.find((format) => format.val === formatName);
                columns.push({
                    header: groups.header.trim(),
                    variable,
                    width: Number(groups.width) || 1, // Default to 1 if no width is provided
                    format,
                    data: data && { val: data },
                });
            }

            this.tables[i] = columns;
        }
    }

    onSampleClick() {
        if (!this.document.sampleFilename) return;
        DocumentService.downloadSample(this.document.document_id, this.document.sampleFilename);
    }

    @Bind
    async showReportBuilderPreview() {
        if (!this.validatePreviewParameters()) return;

        const { header_text, body_text, footer_text } = this.document;
        const options = {
            group_by: this.document.group_by,
            sort_order: this.document.sort_order,
            page_break: this.document.page_break,
            margins: this.document.margins,
            font_size: this.document.font_size,
            page_orientation: this.document.page_orientation,
            page_header_height: this.document.page_header_height,
            page_footer_height: this.document.page_footer_height,
        };
        const documentRun = await DocumentService.setDocumentRun(null, header_text, body_text, footer_text, options);
        const runId = documentRun.root.itemId;

        const context = { ...this.previewParameters, documentRunId: String(runId) };
        const id = this.document.report_id || Reports.Reports.ReservationsShell.id;
        return this.putReportRequest(id, this.type().label, context);
    }

    parseMargins() {
        if (!this.document.margins) return;
        this.margins = this.document.margins.match(/[\d.]+/g)?.map(Number) ?? this.margins;
    }

    onMarginsChange() {
        this.document.margins = `${this.margins[0]}in ${this.margins[1]}in ${this.margins[2]}in ${this.margins[3]}in`;
    }

    getDefaultSortOrder(type: string) {
        return Doc.reportBuilderDefaultSort[type as keyof typeof Doc.reportBuilderDefaultSort]?.join(",") ?? "";
    }

    parseSortOrder() {
        if (!this.document.sort_order) return;
        const sortOrder = (this.document.sort_order.match(/[\w_]+/g) as string[]) ?? [];
        while (sortOrder.length < 3) sortOrder.push("none");

        const variables = BpeVarsConst.Items[this.type().id as keyof typeof BpeVarsConst.Items];
        this.sortOrders = sortOrder.map((val) => {
            if (val === "none") return { val, txt: "None" };
            const variable = variables?.find((variable) => "val" in variable && variable.val === val);
            if (variable) return { ...(variable as { val: string; txt: string }) };
            return { val, txt: "None" };
        });
    }

    onSortOrderChange() {
        this.sortOrders.sort((a, b) => {
            if (a.val === "none" && b.val === "none") return 0;
            if (a.val === "none") return 1;
            if (b.val === "none") return -1;
            return 0;
        });
        this.document.sort_order = this.sortOrders
            .filter((order) => order.val !== "none")
            .map((order) => order.val)
            .join(",");
    }

    parseApplyDateRange() {
        const name = Object.fromEntries(this.applyDateRangeOptions.map((item) => [item.itemId, item.itemName]));
        this.applyDateRange = this.document.apply_date_range
            ?.split(",")
            ?.map((item) => ({ itemId: item, itemName: name[item] }));
    }

    onApplyDateRangeChange(items: DropDownItem[]) {
        this.document.apply_date_range = items.map((item) => item.itemId).join(",");
    }

    protected readonly Report = Report;
}
